import { Container, Group, SegmentedControl } from '@mantine/core';
import classes from './Header.module.css';
import logo from '../../assets/montosa_home_icon.svg';
import { useLocation, Link } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../utils/analytics.ts';

export default function Header() {
    const location = useLocation();
    const { t, i18n } = useTranslation();

    // Persisted language (localStorage), fallback to i18next resolved language, then 'en'
    const [lang, setLang] = useState<string>(() => {
        const saved = typeof window !== 'undefined' ? localStorage.getItem('lang') : null;
        return saved || i18n.resolvedLanguage || 'en';
    });

    useEffect(() => {
        // Apply language change
        i18n.changeLanguage(lang);
        // Persist for future visits
        if (typeof window !== 'undefined') localStorage.setItem('lang', lang);
        // Update <html lang="..."> for accessibility/SEO
        if (typeof document !== 'undefined') document.documentElement.lang = lang;
    }, [lang, i18n]);

    // Build links with live translation (don’t translate at module scope)
    const links = useMemo(
        () => [
            { link: '/about', label: t('About us') },
            // add more links here as needed
        ],
        [t]
    );

    const handleNavClick = (label: string, destination: string) => {
        trackEvent('nav_click', { category: 'Navigation', label, destination });
    };

    return (
        <header className={classes.header}>
            <Container size="xl" className={classes.inner}>
                <Link
                    to="/"
                    className={classes.brand}
                    aria-label="Promonsa home"
                    onClick={() => handleNavClick('Logo', '/')}
                >
                    <img alt="Promonsa logo" src={logo} className={classes.logo} />
                </Link>

                <Group gap={8} className={classes.right} wrap="nowrap">
                    <nav className={classes.nav}>
                        {links.map((item) => (
                            <Link
                                key={item.link}
                                to={item.link}
                                className={classes.link}
                                aria-label={item.label}
                                aria-current={location.pathname === item.link ? 'page' : undefined}
                                data-active={location.pathname === item.link || undefined}
                                onClick={() => handleNavClick(item.label, item.link)}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Language picker */}
                    <SegmentedControl
                        className={classes.lang}
                        size="xs"
                        radius="md"
                        value={lang}
                        onChange={setLang}
                        aria-label={t('Language')}
                        data={[
                            { label: 'EN', value: 'en' },
                            { label: 'ES', value: 'es' },
                        ]}
                    />
                </Group>
            </Container>
        </header>
    );
}
